import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Check, Globe } from "lucide-react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { useApp, CURRENCIES, LANGUAGES } from "@/context/AppContext";

function Logo() {
  return (
    <span className="inline-flex items-center leading-none" data-testid="logo">
      <img
        src="/orangebysite-logo-white-black-v2.webp"
        alt="ORANGEBYSITE"
        className="h-12 md:h-[64px] w-auto select-none object-contain block"
        draggable={false}
      />
    </span>
  );
}

function PillDropdown({ testid, label, icon, children }) {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          data-testid={testid}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border border-black/15 hover:bg-white text-[12px] font-semibold text-black tracking-wide transition-colors min-w-[64px] justify-center"
        >
          {icon}
          <span>{label}</span>
          <ChevronDown size={13} className="opacity-70" />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          sideOffset={10}
          align="end"
          className="z-[60] min-w-[180px] rounded-2xl border border-black/15 bg-white p-1.5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)]"
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

function DropdownRow({ selected, onSelect, label, suffix, testid }) {
  return (
    <DropdownMenuPrimitive.Item
      data-testid={testid}
      onSelect={onSelect}
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-[13px] text-black hover:bg-black/5 cursor-pointer outline-none"
    >
      <span className="flex items-center gap-2">
        {label}
        {suffix && <span className="text-black/50 text-[11px]">{suffix}</span>}
      </span>
      {selected && <Check size={14} className="text-black" />}
    </DropdownMenuPrimitive.Item>
  );
}

// Uppercase nav item — active gets white underline
function NavItem({ label, active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="relative inline-flex items-center gap-1 text-[12px] font-bold text-black uppercase tracking-[0.18em] hover:text-black transition-colors"
    >
      {label}
      {children}
      <span
        className={`absolute left-0 right-0 -bottom-1.5 h-[2px] rounded-full transition-all ${
          active ? "bg-white opacity-100" : "bg-white opacity-0"
        }`}
      />
    </button>
  );
}

// Websites dropdown
function WebsitesDropdown({ onNavigate, t }) {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          data-testid="nav-link-websites"
          className="relative inline-flex items-center gap-1 text-[12px] font-bold text-black uppercase tracking-[0.18em] hover:text-black transition-colors"
        >
          {t("nav.websites")}
          <ChevronDown size={14} className="opacity-80" strokeWidth={2.4} />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          sideOffset={12}
          align="center"
          className="z-[60] min-w-[260px] rounded-2xl border border-black/15 bg-white p-1.5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)]"
        >
          <DropdownMenuPrimitive.Item
            data-testid="nav-link-websites-crm"
            onSelect={() => onNavigate("#crm-service")}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-black hover:bg-black/5 cursor-pointer outline-none uppercase tracking-[0.14em]"
          >
            CRM & Business Systems
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item
            data-testid="nav-link-websites-custom"
            onSelect={() => onNavigate("#websites")}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-black hover:bg-black/5 cursor-pointer outline-none uppercase tracking-[0.14em]"
          >
            Custom Websites
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item
            data-testid="nav-link-websites-landing"
            onSelect={() => onNavigate("#landing")}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-black hover:bg-black/5 cursor-pointer outline-none uppercase tracking-[0.14em]"
          >
            Landing Pages
          </DropdownMenuPrimitive.Item>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

// Products dropdown — extensible list, add future OrangeBySite products here.
const PRODUCT_ITEMS = [
  { key: "orangecare", label: "OrangeCare", href: "#care", external: false },
  { key: "optimise",   label: "Orange Optimise", href: "/optimise", external: true },
];

function ProductsDropdown({ onNavigate }) {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          data-testid="nav-link-products"
          className="relative inline-flex items-center gap-1 text-[12px] font-bold text-black uppercase tracking-[0.18em] hover:text-black transition-colors"
        >
          Products
          <ChevronDown size={14} className="opacity-80" strokeWidth={2.4} />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          sideOffset={12}
          align="center"
          className="z-[60] min-w-[220px] rounded-2xl border border-black/15 bg-white p-1.5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)]"
        >
          {PRODUCT_ITEMS.map((p) => (
            <DropdownMenuPrimitive.Item
              key={p.key}
              data-testid={`nav-link-products-${p.key}`}
              onSelect={() => { if (p.external) { window.location.href = p.href; } else { onNavigate(p.href); } }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-black hover:bg-black/5 cursor-pointer outline-none uppercase tracking-[0.14em]"
            >
              {p.label}
            </DropdownMenuPrimitive.Item>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const { currency, setCurrency, language, setLanguage, t } = useApp();

  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    // Cross-route support (Phase 3I.4O.4) — when the Nav is rendered on a
    // page that is NOT the homepage (e.g. /optimise), a hash cannot resolve
    // to an element on THIS page, so we bounce back to the homepage first
    // and let PublicSite's hash-scroll effect take over on arrival.
    const onHome = window.location.pathname === "/";
    // HOME → true top of the page (above the promo banner)
    if (href === "#top" || href === "#home") {
      if (!onHome) { window.location.href = "/"; return; }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // External / absolute routes.
    if (href && href.startsWith("/") && !href.startsWith("/#")) {
      window.location.href = href;
      return;
    }
    if (!onHome && href && href.startsWith("#")) {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (!el) return;
    const header = document.querySelector('[data-testid="fixed-header"]');
    const headerH = header ? header.getBoundingClientRect().height : 132;
    const rect = el.getBoundingClientRect();
    const y = rect.top + window.pageYOffset - headerH - 8;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  // Mobile IA mirrors the new desktop hierarchy.
  const mobileGroups = [
    { key: "home", label: t("nav.home"), href: "#top" },
    { key: "websites", label: "Websites", children: [
      { key: "websites_crm",     label: "CRM & Business Systems", href: "#crm-service" },
      { key: "websites_custom",  label: "Custom Websites",        href: "#websites" },
      { key: "websites_landing", label: "Landing Pages",          href: "#landing" },
    ]},
    { key: "products", label: "Products", children: [
      { key: "orangecare", label: "OrangeCare",       href: "#care" },
      { key: "optimise",   label: "Orange Optimise",  href: "/optimise", external: true },
    ]},
    { key: "portfolio", label: "Portfolio", href: "#work" },
    { key: "contact",   label: "Contact Us", href: "#discovery" },
  ];

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-30 w-full"
      style={{
        background:
          "radial-gradient(ellipse 200% 400% at 58% 45%, #FFB347 0%, #FF8A2E 30%, #FF7A00 55%, #FF6A00 80%, #D94C00 100%)",
        boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
      }}
    >
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-[80px] flex items-center gap-4">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="shrink-0"
        >
          <Logo />
        </button>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-6 xl:gap-8">
          <NavItem testid="nav-link-home" label={t("nav.home")} active={atTop} onClick={() => go("#top")} />
          <WebsitesDropdown onNavigate={go} t={t} />
          <ProductsDropdown onNavigate={go} />
          <NavItem testid="nav-link-portfolio" label="Portfolio" onClick={() => go("#work")} />
          <NavItem testid="nav-link-contact" label="Contact Us" onClick={() => go("#discovery")} />
        </nav>

        <div className="flex items-center gap-2 ml-auto lg:ml-0 shrink-0">
          <div className="hidden md:flex items-center gap-2">
            <PillDropdown testid="nav-currency-trigger" label={currency}>
              {CURRENCIES.map((c) => (
                <DropdownRow
                  key={c.code}
                  testid={`nav-currency-${c.code}`}
                  selected={currency === c.code}
                  onSelect={() => setCurrency(c.code)}
                  label={c.code}
                  suffix={c.symbol.trim()}
                />
              ))}
            </PillDropdown>
            <PillDropdown
              testid="nav-language-trigger"
              label={currentLang.short}
              icon={<Globe size={13} className="opacity-80" />}
            >
              {LANGUAGES.map((l) => (
                <DropdownRow
                  key={l.code}
                  testid={`nav-language-${l.code}`}
                  selected={language === l.code}
                  onSelect={() => setLanguage(l.code)}
                  label={l.label}
                  suffix={l.short}
                />
              ))}
            </PillDropdown>
          </div>

          <button
            data-testid="nav-cta-enquire"
            onClick={() => go("#discovery")}
            className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-black text-white text-[12px] font-bold uppercase tracking-[0.14em] hover:opacity-90 transition-opacity"
          >
            {t("nav.cta")}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full bg-black text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {mobileGroups.map((g) => (
                g.children ? (
                  <div key={g.key} className="py-2 border-b border-black/10" data-testid={`nav-mobile-group-${g.key}`}>
                    <div className="text-[11px] font-black text-black/60 uppercase tracking-[0.24em] py-2">{g.label}</div>
                    <div className="pl-2 flex flex-col gap-1">
                      {g.children.map((c) => (
                        <button
                          key={c.key}
                          data-testid={`nav-mobile-link-${c.key}`}
                          onClick={() => { if (c.external) { window.location.href = c.href; } else { go(c.href); } }}
                          className="text-left text-[13px] font-bold text-black py-2 uppercase tracking-[0.18em] hover:text-[#FF6A00]"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    key={g.key}
                    data-testid={`nav-mobile-link-${g.key}`}
                    onClick={() => go(g.href)}
                    className="text-left text-[13px] font-bold text-black py-3 border-b border-black/10 uppercase tracking-[0.18em]"
                  >
                    {g.label}
                  </button>
                )
              ))}
              <div className="flex items-center gap-2 pt-5">
                <PillDropdown testid="mobile-currency-trigger" label={currency}>
                  {CURRENCIES.map((c) => (
                    <DropdownRow
                      key={c.code}
                      testid={`mobile-currency-${c.code}`}
                      selected={currency === c.code}
                      onSelect={() => setCurrency(c.code)}
                      label={c.code}
                      suffix={c.symbol.trim()}
                    />
                  ))}
                </PillDropdown>
                <PillDropdown
                  testid="mobile-language-trigger"
                  label={currentLang.short}
                  icon={<Globe size={13} className="opacity-80" />}
                >
                  {LANGUAGES.map((l) => (
                    <DropdownRow
                      key={l.code}
                      testid={`mobile-language-${l.code}`}
                      selected={language === l.code}
                      onSelect={() => setLanguage(l.code)}
                      label={l.label}
                      suffix={l.short}
                    />
                  ))}
                </PillDropdown>
                <button
                  data-testid="nav-mobile-cta"
                  onClick={() => go("#discovery")}
                  className="ml-auto inline-flex items-center gap-2 h-10 px-5 rounded-full bg-black text-white text-[12px] font-bold uppercase tracking-[0.14em]"
                >
                  {t("nav.cta")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
